import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Thermometer } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const UValueExamples = () => {
  return (
    <>
      <Helmet>
        <title>U-Values Explained with Examples | Building Regulations Guide | Energy Performance Direct</title>
        <meta name="description" content="Practical guide to U-values with real examples. What U-values mean, Part L requirements for walls, floors, roofs and windows, and how to achieve compliance." />
        <meta name="keywords" content="U-value examples, U-value explained, Part L U-values, wall U-value, floor U-value, roof U-value, window U-value, thermal transmittance" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/resources/u-value-examples" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <Link to="/resources" className="text-green-600 hover:underline text-sm">← Back to Resources</Link>
              </div>
              
              <header className="mb-12">
                <span className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">Technical</span>
                <h1 className="text-4xl font-bold text-gray-900 mt-4 mb-6">U-Values Explained with Practical Examples</h1>
                <p className="text-xl text-gray-600">A straightforward guide to U-values - what they measure, what the regulations require, and real-world examples for common building elements.</p>
              </header>

              <section className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold mt-8 mb-4">What is a U-Value?</h2>
                <p className="text-gray-700 mb-4">
                  A U-value (thermal transmittance) measures how effective a building element is as an insulator. It represents the rate of heat transfer through a structure, measured in <strong>W/m²K</strong> (watts per square metre per degree Kelvin).
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-6">
                  <Thermometer className="h-8 w-8 text-blue-600 mb-3" />
                  <p className="text-blue-800 font-medium">Key Principle: Lower U-values = Better Insulation</p>
                  <p className="text-blue-700 mt-2">A wall with U-value 0.18 loses heat half as fast as one with U-value 0.36.</p>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Part L 2021 Requirements</h2>
                <p className="text-gray-700 mb-4">Building Regulations Part L sets maximum (limiting) U-values. However, to achieve the overall energy targets, you typically need to do better than the limits.</p>
                
                <div className="overflow-x-auto my-6">
                  <table className="w-full border rounded-lg overflow-hidden">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Element</th>
                        <th className="px-4 py-3 text-left font-semibold">Limiting Value</th>
                        <th className="px-4 py-3 text-left font-semibold">Typical Target</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr><td className="px-4 py-3">External Walls</td><td className="px-4 py-3">0.26 W/m²K</td><td className="px-4 py-3">0.18-0.22 W/m²K</td></tr>
                      <tr className="bg-gray-50"><td className="px-4 py-3">Ground Floors</td><td className="px-4 py-3">0.18 W/m²K</td><td className="px-4 py-3">0.13-0.15 W/m²K</td></tr>
                      <tr><td className="px-4 py-3">Roofs</td><td className="px-4 py-3">0.16 W/m²K</td><td className="px-4 py-3">0.11-0.13 W/m²K</td></tr>
                      <tr className="bg-gray-50"><td className="px-4 py-3">Windows</td><td className="px-4 py-3">1.6 W/m²K</td><td className="px-4 py-3">1.2-1.4 W/m²K</td></tr>
                      <tr><td className="px-4 py-3">External Doors</td><td className="px-4 py-3">1.6 W/m²K</td><td className="px-4 py-3">1.0-1.4 W/m²K</td></tr>
                    </tbody>
                  </table>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Real-World Examples</h2>
                
                <h3 className="text-xl font-semibold mt-6 mb-3">Wall U-Value Examples</h3>
                <div className="space-y-4 mb-6">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">Traditional Cavity Wall (uninsulated)</h4>
                          <p className="text-sm text-gray-600">Brick-cavity-block, no insulation</p>
                        </div>
                        <span className="text-red-600 font-bold">~1.5 W/m²K</span>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">Filled Cavity Wall (100mm PIR)</h4>
                          <p className="text-sm text-gray-600">Brick-100mm full-fill-block</p>
                        </div>
                        <span className="text-yellow-600 font-bold">~0.22 W/m²K</span>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">Timber Frame Wall (140mm + 50mm)</h4>
                          <p className="text-sm text-gray-600">Typical timber frame with service void</p>
                        </div>
                        <span className="text-green-600 font-bold">~0.15 W/m²K</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <h3 className="text-xl font-semibold mt-6 mb-3">Floor U-Value Examples</h3>
                <div className="space-y-4 mb-6">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">Solid Concrete Floor (100mm XPS)</h4>
                          <p className="text-sm text-gray-600">Slab with 100mm insulation below</p>
                        </div>
                        <span className="text-green-600 font-bold">~0.15 W/m²K</span>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">Beam & Block (150mm mineral wool)</h4>
                          <p className="text-sm text-gray-600">Suspended floor with insulation between joists</p>
                        </div>
                        <span className="text-yellow-600 font-bold">~0.18 W/m²K</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <h3 className="text-xl font-semibold mt-6 mb-3">Roof U-Value Examples</h3>
                <div className="space-y-4 mb-6">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">Cold Roof (300mm loft insulation)</h4>
                          <p className="text-sm text-gray-600">Mineral wool at ceiling level</p>
                        </div>
                        <span className="text-green-600 font-bold">~0.13 W/m²K</span>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">Warm Flat Roof (120mm PIR)</h4>
                          <p className="text-sm text-gray-600">Insulation above deck</p>
                        </div>
                        <span className="text-green-600 font-bold">~0.15 W/m²K</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">How U-Values Affect SAP Ratings</h2>
                <p className="text-gray-700 mb-4">
                  Better U-values directly improve your SAP rating by reducing calculated heat loss. The impact depends on element area - improving large wall areas has more effect than small windows.
                </p>

                <h2 className="text-2xl font-bold mt-8 mb-4">Related Services</h2>
                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <Link to="/u-value-calculations" className="block">
                    <Card className="h-full hover:shadow-lg transition-shadow hover:border-green-300">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">U-Value Calculations</h3>
                        <p className="text-gray-600 text-sm">Professional thermal transmittance calculations</p>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/resources/psi-values-explained" className="block">
                    <Card className="h-full hover:shadow-lg transition-shadow hover:border-green-300">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">Psi Values Guide</h3>
                        <p className="text-gray-600 text-sm">Understanding thermal bridging at junctions</p>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </section>

              <div className="mt-12 p-6 bg-green-50 rounded-lg text-center">
                <h3 className="text-xl font-bold mb-3">Need U-Value Calculations?</h3>
                <p className="text-gray-600 mb-4">We calculate U-values for walls, floors, roofs and non-standard constructions.</p>
                <Link to="/booking">
                  <Button className="bg-green-600 hover:bg-green-700">
                    Request Quote <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default UValueExamples;
